import React, { useState } from 'react';
import { styled } from '@glitz/react';
import StoreStockViewModel from 'Product/StoreStockViewModel.type';
import { CircleFilled8x8 } from 'Shared/Icons/Circle';
import {
  H3,
  Paragraph,
  ResponsiveSize,
  ResponsiveLineHeight,
  Text,
  TextLabel,
} from 'Shared/SharedComponents/atoms/Typography';
import { AvailableSizes } from 'Product/Sizes';
import StoreInformation from './StoreInformation';
import StoreAvailabilityViewModelLabels from 'Product/VariationViewModel+StoreAvailabilityViewModelLabels.type';
import { Plus12x12 } from 'Shared/Icons/Plus';
import { colorFromStatus } from 'Shared/utils';
import * as style from 'Shared/Style';

type ConnectedPropType = {
  storeAvailability: StoreStockViewModel;
  isSelected: boolean;
  labels: StoreAvailabilityViewModelLabels;
  isCompact: boolean;
  onClick: () => void;
};

const Wrap = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '16px',
  padding: { x: '12px', top: '12px', bottom: '16px' },
  border: {
    xy: {
      width: '2px',
      style: 'solid',
    },
  },
  borderRadius: '8px',
})

const StoreDataWrap = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  ['@media ' + style.mediaMinLarge]: {
    flexDirection: 'row'
  },
})

const StoreDataItem1 = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '9px',
  ['@media ' + style.mediaMinLarge]: {
    flex: { basis: '50%' },
    padding: { right: '60px' },
  },
})

const StoreDataItem1Row = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: '42px', 
  flexGrow: 0,
  ['@media ' + style.mediaMinLarge]: {
    gap: '8px', 
  },
})

const StoreDataItem1Text = styled(Paragraph, {
  color: style.colors.monochrome.a11ygrey, 
  flexGrow: 1
})

const StoreDataItem2 = styled.div({
  display: 'flex',
  flexDirection: 'column',
  ['@media ' + style.mediaMinLarge]: {
    flex: { basis: '50%' },
  },
})

const StoreDataItem2Row = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'baseline',
  gap: '8px',
  whiteSpace: 'nowrap',
  padding: { top: '24px', bottom: '8px' },
  ['@media ' + style.mediaMinLarge]: {
    padding: { top: '5px', bottom: '11x' },
  },
})

const Plus = styled(Plus12x12, {
  marginLeft: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'inherit',
  transitionDuration: '0.2s',
})

const PlusRotaded = styled(Plus, {
  transform: 'rotate(45deg)',
})

const MoreInformationMobile = styled.div({
  cursor: 'pointer', margin: { bottom: '16px' }, display: 'flex'
})

const MoreInformationDesktop = styled.div({
  cursor: 'pointer', display: 'flex'
})

const SelectedStore = styled(Text, {
  color: style.colors.monochrome.a11ygrey,
  whiteSpace: 'nowrap',
  padding: { top: '3px' },
  ['@media ' + style.mediaMinLarge]: {
    padding: { top: '5px' },
  },
})

const AvailableSizesMobile = styled.div({
  cursor: 'pointer', 
  display: 'flex'
})

type PropType = ConnectedPropType;

function StoreAvailabilityCard({ storeAvailability, isSelected, labels, isCompact, onClick }: PropType) {
  const [moreInformationShown, setMoreInformationShown] = useState(false);
  const [availableSizesShown, setAvailableSizesShown] = useState(false);

  return (
    <Wrap
      onClick={onClick}
      css={{
        border: {
          xy: {
            color: isSelected ? style.colors.monochrome.black : style.colors.monochrome.lightGrey,
          },
        },
      }}
    >
      <StoreDataWrap>
        <StoreDataItem1>
          <StoreDataItem1Row>
            <H3 upperCase fontSize={ResponsiveSize.D20_M16}>{storeAvailability.name}</H3>
            {isSelected && (
              <SelectedStore
                fontSize={ResponsiveSize.D12_M12}
                lineHeight={ResponsiveLineHeight.D120_M120}
              >
                {labels.selectedStore}
              </SelectedStore>
            )}
          </StoreDataItem1Row>
          <StoreDataItem1Text fontSize={ResponsiveSize.D14_M14_L130}>
            {storeAvailability.address}
            <styled.Br />
            {storeAvailability.zipCode} {storeAvailability.city}
          </StoreDataItem1Text>
          {!isCompact && (
            <MoreInformationDesktop
              className="store-availability-card__more-information-title"
              onClick={() => setMoreInformationShown((infoShown) => !infoShown)}
            >
              <TextLabel css={{ flexGrow: 0 }}>{labels.moreInformation}</TextLabel>
              {moreInformationShown ? <PlusRotaded /> : <Plus />}
            </MoreInformationDesktop>
          )}
        </StoreDataItem1>
        <StoreDataItem2>
          <StoreDataItem2Row>
            <CircleFilled8x8 css={{color: colorFromStatus(storeAvailability.availabilityStatus)}} />
            <Paragraph fontSize={ResponsiveSize.D12_M12} lineHeight={ResponsiveLineHeight.D120_M120}>
              {storeAvailability.availabilityLabel}
            </Paragraph>
          </StoreDataItem2Row>
          {!isCompact && <AvailableSizes availableSizes={storeAvailability.availability} />}
        </StoreDataItem2>
      </StoreDataWrap>
      {isCompact && (
        <AvailableSizesMobile
          className="store-availability-card__available-sizes-title"
          onClick={() => setAvailableSizesShown((availableSizesShown) => !availableSizesShown)}
        >
          <TextLabel>{labels.availableSizes}</TextLabel>
          {availableSizesShown ? <PlusRotaded /> : <Plus />}
        </AvailableSizesMobile>
      )}
      {isCompact && availableSizesShown && <AvailableSizes availableSizes={storeAvailability.availability} />}
      <styled.Div>
        {isCompact && (
          <MoreInformationMobile
            className="store-availability-card__more-information-title"
            onClick={() => setMoreInformationShown((infoShown) => !infoShown)}
          >
            <TextLabel>{labels.moreInformation}</TextLabel>
            {moreInformationShown ? <PlusRotaded /> : <Plus />}
          </MoreInformationMobile>
        )}
        <StoreInformation
          show={moreInformationShown}
          labels={{
            openingHours: labels.openHours,
            openingHoursExceptions: labels.deviantOpenHours,
            contact: labels.contact,
            findStore: labels.directions,
          }}
          openingHours={storeAvailability.openingHours}
          openingHoursExceptions={storeAvailability.openingHoursDeviating}
          phoneNumber={storeAvailability.phoneNumber}
          directionsLink={storeAvailability.directionsLink}
        />
      </styled.Div>
    </Wrap>
  );
}

export default StoreAvailabilityCard;
